import { FormBuilder } from '@/components/formBuilder';
import { SelectWithDescriptionOption } from '@/components/formBuilder/SelectComponents';
import { KText } from '@/ui-library/components';
import { downloadFileFromUrl, toDocxFilename } from '@/utils/commonHelpers';
import { Box, Center, Flex, useDisclosure } from '@chakra-ui/react';
import { CheckCircle, File04, XClose, Eye } from '@kula-ai/icons-core';
import { useOfferDocPreview } from '@/components/ats/FileUpload/useOfferDocPreview';
import { DocErrorAsset } from '@kula-ai/assets';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash-es';
import { CenterSpinner } from '@/components/ats';
import {
  extractTokenArrays,
  getFilledValues,
  getUpdatedTokens,
} from '@/components/ats/OfferInitiation/offerFormHelpers';
import { useReplacementUploader } from '@/components/ats/OfferInitiation/PreviewOffer/useReplacementUploader';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import {
  offerDetailsAPI,
  offerDetailsKeys,
  useGetAllOfferTemplatesQuery,
  useUploadReplacementMutation,
  useRemoveOfferDocumentMutation,
  useGetApprovalSettingsQuery,
} from '../../api';
import { TokenValidation } from './TokenValidation';
import { DocumentPreview } from './DocumentPreview';
import { PDFReaderBrowser } from '@/components/file-viewers/reactPdf';
import { EmptyResults } from '@/components/ats/empty-results/EmptyResults';
import { EmptyTokensForm } from './EmptyTokensForm';
import { EmptyTokensWarning } from './EmptyTokensWarning';
import { DocumentTabs } from './DocumentTabs';
import { AddMoreFilesButton } from './AddMoreFilesButton';
import { DocViewerToolbar } from './DocViewerToolbar';
import type { TemplatesFormProps } from './TemplatesForm.interface';
import { useOfferStore } from '../../store';
import { useSelector } from 'react-redux';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import { useKFlags } from '@/hooks/use-k-flags';
import { useShowToast } from '@/hooks';
import { ModalWithForm } from '@/components/ats';
import {
  readJobWorkTypeId,
  useWorkTypeOptions,
  workTypeScopeParam,
} from '@/api/work-types';

const STATUS_CONFIG = {
  unverified: 'This template has unverified tokens',
  error: 'This template has invalid tokens',
  verified: 'All tokens verified',
  no_tokens: 'No tokens found',
};

export const NewTemplatesForm = ({
  templatesFormRef,
  initialValues,
  detailsFormRef,
  tabIndex,
  currentOffer,
  detailsFormConfig,
  templateProps,
  setTemplateProps,
  onTemplateSelect,
  initialDepartmentId,
  initialOfficeId,
  sendMode = false,
  existingFormValues = {},
  validationValues,
  onEditOffer,
}: TemplatesFormProps) => {
  const [regenerateKey, setRegenerateKey] = useState(0);
  const [generationErrorIndices, setGenerationErrorIndices] = useState<
    Set<number>
  >(new Set());
  const [isTemplateSelectionComplete, setIsTemplateSelectionComplete] =
    useState(true);
  const [sendModeTokensFilled, setSendModeTokensFilled] = useState(false);
  // Values the user filled via the inline empty-tokens form this session, accumulated
  // across template switches. In send mode these overlay the offer's raw values so a
  // token stays "filled" after a switch without relying on the defaults-laden form ref.
  const [inlineSavedValues, setInlineSavedValues] = useState<
    Record<string, any>
  >({});
  // Send-mode fill-source: raw offer values (no FE structural defaults) + inline saves.
  // Undefined outside send mode, so getFilledValues keeps its normal form-ref behaviour.
  const sendModeFillValues = useMemo(
    () =>
      sendMode
        ? { ...(validationValues || {}), ...inlineSavedValues }
        : undefined,
    [sendMode, validationValues, inlineSavedValues]
  );
  // Whether the user has clicked "Edit offer" in the warning card to reveal the inline form.
  const [showEditForm, setShowEditForm] = useState(false);
  const [pendingReplaceFile, setPendingReplaceFile] = useState<File | null>(
    null
  );
  // True once the PDF preview's own toolbar is on screen; hides the fallback toolbar.
  const [isPreviewReady, setIsPreviewReady] = useState(false);
  // Signals DocumentPreview to drop a removed doc's PDF locally (no regenerate).
  const [pdfRemoval, setPdfRemoval] = useState({ index: -1, nonce: 0 });
  const esignReplaceDisclosure = useDisclosure();
  const setHasValidTokens = useOfferStore((state) => state.setHasValidTokens);
  const setCurrentOffer = useOfferStore((state) => state.setCurrentOffer);
  const isGenerating = useOfferStore((state) => state.isPolling);
  const hasInitializedRef = useRef(false);
  const offerPayload = useOfferStore((state) => state.offerPayload || null);
  const setOfferPayload = useOfferStore((state) => state.setOfferPayload);
  const { currentJob } = useSelector(getAtsJobCreate);
  const jobEmploymentType = currentJob?.data?.basic_info?.employment_type;
  const { isWorkTypesEnabled } = useWorkTypeOptions();
  const jobWorkTypeId = readJobWorkTypeId(currentJob?.data?.basic_info ?? {});
  const flags = useKFlags();
  const multiDocsEnabled = !!flags.tOfferTemplateMultiDocuments;
  const toast = useShowToast();

  // Server-side template search. We fetch a small page and let the BE filter by
  // name (filter_by[ats_offer_template_name]) so accounts with large template
  // libraries aren't capped by a fixed client-side page (ENG-3330: the 101st
  // matching template never loaded under the old items:100 client-side search).
  const [templateSearch, setTemplateSearch] = useState('');

  const filterParams = useMemo(
    () => {
      if (tabIndex === 0) return null;
      const trimmedSearch = templateSearch.trim();
      return {
        filter_by: {
          department_ids:
            detailsFormRef?.current?.values?.ats_department_id ??
            initialDepartmentId,
          office_ids:
            detailsFormRef?.current?.values?.ats_office_id ?? initialOfficeId,
          // Scope the template list to the job's work type. Flag off keeps the
          // legacy enum. BACKEND REQUIRED: filter_by on /ats_offer_templates
          // slices to %i[department_ids office_ids employment_type
          // ats_offer_template_name], so flag-on the key is dropped and the
          // list comes back unnarrowed — the same result a custom-typed job
          // gets today, since its employment_type is null.
          ...workTypeScopeParam(
            isWorkTypesEnabled,
            {
              atsWorkTypeIds: [jobWorkTypeId],
              employmentTypes: [jobEmploymentType],
            },
            // This endpoint slices on the singular key.
            { legacyKey: 'employment_type' }
          ),
          ...(trimmedSearch && { ats_offer_template_name: trimmedSearch }),
        },
        // Newest template first so a freshly-created one surfaces at the top of
        // the default page (QA: latest-created should be immediately visible).
        sort_by: { created_at: 'DESC' },
        items: 20,
      };
    },
    [
      tabIndex,
      jobEmploymentType,
      initialDepartmentId,
      initialOfficeId,
      templateSearch,
    ] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const { data: templatesData, isFetching } = useGetAllOfferTemplatesQuery(
    filterParams ?? {},
    { enabled: filterParams !== null }
  );

  // Debounced search wired to the select's input. react-select fires onInputChange
  // for typing ('input-change') as well as blur/menu-close/set-value; only real
  // typing should hit the BE, and closing the menu resets to the default page.
  const debouncedSetSearch = useMemo(
    () => debounce((text: string) => setTemplateSearch(text), 300),
    []
  );
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const handleTemplateInputChange = useCallback(
    (text: string, meta?: { action?: string }) => {
      if (meta?.action === 'input-change') {
        debouncedSetSearch(text ?? '');
      } else if (
        meta?.action === 'menu-close' ||
        meta?.action === 'input-blur'
      ) {
        debouncedSetSearch.cancel();
        setTemplateSearch('');
      }
    },
    [debouncedSetSearch]
  );
  const templates = useMemo(() => templatesData?.data || [], [templatesData]);
  const currentTemplate = templateProps?.template_value;

  // Multi-doc: the BE returns offer_documents only when its flag is on, so the
  // array's presence is the source of truth. Tabs switch which doc's tokens show.
  // When flag is on but the template only has offer_document (single-doc path),
  // synthesize a single-item array so the doc rail renders instead of the old file card.
  const docs = useMemo(() => {
    if ((currentTemplate?.offer_documents?.length ?? 0) > 0)
      return currentTemplate.offer_documents;
    if (multiDocsEnabled && currentTemplate?.offer_document)
      return [currentTemplate.offer_document];
    return [];
  }, [currentTemplate, multiDocsEnabled]);
  // isMultiDoc: true only for real multi-doc (controls API documentIndex + per-doc tokens)
  const isMultiDoc =
    multiDocsEnabled && (currentTemplate?.offer_documents?.length ?? 0) > 0;
  // useDocNav: true whenever the flag is on and we have at least one doc in the rail
  const useDocNav = multiDocsEnabled && docs.length > 0;
  const [selectedDocIndex, setSelectedDocIndex] = useState(0);
  const selectedDoc = docs[selectedDocIndex];
  // Faithful server-rendered PDF preview of the selected template .docx (shared
  // with the other offer surfaces) — avoids the mangled client-side docx render.
  const { openPreview, previewNode } = useOfferDocPreview();
  const previewSelectedDoc = () => {
    const doc = selectedDoc || currentTemplate?.offer_document;
    if (!doc?.url) return;
    openPreview({
      url: doc.url,
      filename: doc.filename ?? 'offer_document.docx',
      previewUrl: doc.preview?.url,
    });
  };
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<
    number | null
  >(null);
  const setIsMultiDocTemplate = useOfferStore(
    (state) => state.setIsMultiDocTemplate
  );
  // Files added via "Add more files" — rendered in the doc rail (not the bottom bar) for multi-doc.
  const offerAttachments = useOfferStore((state) => state.offerAttachments);
  const selectedAttachment = offerAttachments.find(
    (a) => a.id === selectedAttachmentId
  );
  const removeOfferAttachment = useOfferStore(
    (state) => state.removeOfferAttachment
  );

  const handleRemoveAttachment = async (attachmentId: number) => {
    await offerDetailsAPI.deleteAttachment({
      offerId: currentOffer?.id,
      attachmentId,
    });
    removeOfferAttachment(attachmentId);
    toast({ title: 'Document removed', status: 'success' });
  };

  // The offer may sit on a per-offer copy (kind "single"); match it back to a library template by
  // its document set so the dropdown stays meaningful, otherwise surface it as "Custom".
  const dropdownTemplateValue = useMemo(() => {
    if (!currentTemplate?.id) return undefined;
    if (currentTemplate.kind === 'inline') return currentTemplate.id;
    // Per-offer (kind "single") matching only applies to multi-doc. Single-doc / flag-off
    // keeps the original behavior (no forced value, dropdown hidden for a kind "single" copy).
    if (!isMultiDoc) return undefined;
    // The offer records the library template its copy was cloned from; prefer it to
    // the filename match below, which two templates sharing a document set defeat.
    const sourceId = currentOffer?.source_ats_offer_template_id;
    if (sourceId !== null && sourceId !== undefined) {
      const bySource = templates.find((t) => String(t.id) === String(sourceId));
      if (bySource) return bySource.id;
    }
    const currentNames = (currentTemplate.offer_documents || []).map(
      (d) => d.filename
    );
    const match = templates.find((t) => {
      const names = (t.offer_documents || []).map((d) => d.filename);
      return (
        names.length === currentNames.length &&
        names.every((name, i) => name === currentNames[i])
      );
    });
    return match?.id ?? 'custom';
  }, [currentTemplate, templates, isMultiDoc, currentOffer]);

  const isCustomTemplate = dropdownTemplateValue === 'custom';

  // Keep the select showing the matched template (or "Custom") even though the offer is on a
  // per-offer copy whose id isn't in the options list.
  useEffect(() => {
    if (dropdownTemplateValue !== undefined) {
      templatesFormRef?.current?.setFieldValue?.(
        'ats_offer_template_id',
        dropdownTemplateValue
      );
    }
  }, [dropdownTemplateValue, templatesFormRef]);

  useEffect(() => {
    setSelectedDocIndex(0);
  }, [currentTemplate?.id]);

  // Tell the footer whether the panel already shows "Add more files" (multi-doc),
  // so it can hide the footer copy; reset on unmount so the footer button returns.
  useEffect(() => {
    setIsMultiDocTemplate(useDocNav);
    return () => setIsMultiDocTemplate(false);
  }, [useDocNav, setIsMultiDocTemplate]);

  // Extract template ID from initialValues to avoid re-renders from object reference changes
  const initialTemplateId = useMemo(
    () => initialValues?.templates?.ats_offer_template_id,
    [initialValues?.templates?.ats_offer_template_id]
  );
  const initialTemplateValue = useMemo(
    () => initialValues?.templates?.ats_offer_template,
    [initialValues?.templates?.ats_offer_template]
  );

  const queryClient = useQueryClient();

  // Monotonic id: rapid template mutations overlap, so a stale one resolving late
  // must not overwrite the newer template or flip selection complete (bug #1).
  const applyOpSeqRef = useRef(0);

  // Every per-offer template mutation (replace/remove) returns the updated template; treat it as
  // the new source of truth — swap it in, clamp the selected tab, and re-trigger preview.
  const applyTemplateMutation = async (data) => {
    if (!data?.id) return;
    const seq = ++applyOpSeqRef.current;
    setTemplateProps((prev) => ({
      ...prev,
      selected_template_id: data.id,
      template_value: data,
    }));
    const lastIndex = Math.max((data.offer_documents?.length || 1) - 1, 0);
    setSelectedDocIndex((current) => Math.min(current, lastIndex));
    setRegenerateKey((prev) => prev + 1);
    setIsTemplateSelectionComplete(false);
    // Don't reset sendModeTokensFilled: a replace is a doc swap on the same offer whose
    // inline-filled fields aren't in offerPayload; only a full template change resets it.
    try {
      if (onTemplateSelect) {
        // The PUT response carries the complete per-offer template (all docs + tokens).
        // Use it to replace the potentially-incomplete upload response so token display
        // stays accurate after a doc replacement or removal.
        const freshTemplate = await onTemplateSelect(data.id, data);
        // Bail if a newer replace superseded us while the PUT was in flight.
        if (seq !== applyOpSeqRef.current) return;
        if (
          freshTemplate &&
          typeof freshTemplate === 'object' &&
          freshTemplate.id
        ) {
          setTemplateProps((prev) => ({
            ...prev,
            template_value: freshTemplate,
          }));
        }
      }
      setIsTemplateSelectionComplete(true);
    } catch (error) {
      if (seq === applyOpSeqRef.current) setIsTemplateSelectionComplete(true);
      throw error;
    }
  };

  const uploadReplacement = useUploadReplacementMutation({
    onSuccess: applyTemplateMutation,
    onError: (error: { message?: string }) => {
      toast({
        title: 'Failed to replace document',
        description: error?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // Removing a doc doesn't regenerate the whole offer (CORE-4681): swap in the trimmed
  // template, clamp the tab, and tell DocumentPreview to drop the removed PDF locally.
  // templateProps is local useState in OfferLetterTemplate, so a step remount (step 2 -> 1 -> 2)
  // resets it and the doc rail is rebuilt from the store's currentOffer. Any handler that swaps in
  // a new per-offer template must write it there, or the remount re-seeds from the pre-edit
  // template and the change is undone on screen while the BE has already applied it.
  const syncOfferTemplateCache = (data: any) => {
    const offerCacheId = currentOffer?.id;
    if (!offerCacheId) return;
    setCurrentOffer({
      ...currentOffer,
      ats_offer_template_id: data.id,
      ats_offer_template: data,
    });
    queryClient.setQueryData(
      offerDetailsKeys.offerById(offerCacheId),
      (old: any) =>
        old
          ? {
              ...old,
              ats_offer_template_id: data.id,
              ats_offer_template: data,
            }
          : old
    );
  };

  const applyDocumentRemoval = (data: any, removedIndex: number) => {
    if (!data?.id) return;
    setTemplateProps((prev) => ({
      ...prev,
      selected_template_id: data.id,
      template_value: data,
    }));
    const lastIndex = Math.max((data.offer_documents?.length || 1) - 1, 0);
    setSelectedDocIndex((current) => Math.min(current, lastIndex));
    setSelectedAttachmentId(null);
    setPdfRemoval((prev) => ({ index: removedIndex, nonce: prev.nonce + 1 }));
    syncOfferTemplateCache(data);
  };

  // Appending a doc via "Add more files": the add_document response IS the
  // authoritative per-offer template (all docs, each with its tokens), so swap it
  // in directly — mirroring the remove flow. Doing a full updateOffer round-trip
  // here (applyTemplateMutation) re-selected the template and left the doc list
  // stale after step navigation. Bump regenerateKey so the new doc generates once
  // its fields are filled; select it so its token validation shows immediately.
  const applyDocumentAddition = (data: any) => {
    if (!data?.id) return;
    setTemplateProps((prev) => ({
      ...prev,
      selected_template_id: data.id,
      template_value: data,
    }));
    const lastIndex = Math.max((data.offer_documents?.length || 1) - 1, 0);
    setSelectedDocIndex(lastIndex);
    setSelectedAttachmentId(null);
    setRegenerateKey((prev) => prev + 1);
    syncOfferTemplateCache(data);
  };

  const removeOfferDocument = useRemoveOfferDocumentMutation({
    onSuccess: (data, variables: { documentIndex?: number }) => {
      applyDocumentRemoval(data, variables?.documentIndex ?? -1);
      toast({ title: 'Document removed', status: 'success' });
    },
    onError: (error: { message?: string }) => {
      toast({
        title: 'Failed to remove document',
        description: error?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // Replace the currently-selected document. Sends documentIndex whenever the offer has
  // offer_documents (BE flag on), regardless of the FE flag — the BE requires it.
  const replaceSelectedDocument = (file) => {
    if (!file) return;
    uploadReplacement.mutate({
      id: currentOffer?.id,
      file,
      ...(docs.length > 0 ? { documentIndex: selectedDocIndex } : {}),
    });
  };

  // Fallback text-button uploader (used only when the preview — and its built-in Replace — is hidden).
  const handleFileChange = (event) =>
    replaceSelectedDocument(event.target.files?.[0]);

  // PDF viewer "Replace" — mirrors ReplacementUploader: gate on esign confirmation when needed.
  const handleReplaceUpload = (files: File[]) => {
    const file = files?.[0];
    if (!file) return;
    if ((currentOffer as any)?.esign_envelope?.id) {
      setPendingReplaceFile(file);
      esignReplaceDisclosure.onOpen();
    } else {
      replaceSelectedDocument(file);
    }
  };

  const confirmEsignReplace = () => {
    esignReplaceDisclosure.onClose();
    if (pendingReplaceFile) replaceSelectedDocument(pendingReplaceFile);
    setPendingReplaceFile(null);
  };

  const handleRemoveDocument = (index) => {
    removeOfferDocument.mutate({ id: currentOffer?.id, documentIndex: index });
  };

  const { ReplacementUploader } = useReplacementUploader({
    handleFileChange,
    replacedData: uploadReplacement.data,
    isReplacing: uploadReplacement.isPending,
    atsOffer: currentOffer,
  });

  // Set initial template from initialValues (only once)
  useEffect(() => {
    if (!initialTemplateId || hasInitializedRef.current) return;
    setTemplateProps((prev) => ({
      ...prev,
      selected_template_id: initialTemplateId,
      template_value: initialTemplateValue,
    }));
    hasInitializedRef.current = true;
  }, [initialTemplateId, initialTemplateValue, setTemplateProps]);

  // Calculate token validation
  const tokenValidation = useMemo(() => {
    if (!currentTemplate?.id || (!currentTemplate?.tokens && !isMultiDoc)) {
      setHasValidTokens(true);
      return {
        validTokens: [],
        invalidTokens: [],
        filledTokensNotInTemplate: [],
        nonFilledValidTokens: [],
        hasValidationIssues: false,
      };
    }

    // User already filled and saved the empty-token form — treat as valid so
    // the footer reappears and the PDF preview is shown without re-triggering
    // the warning card.
    if (sendModeTokensFilled) {
      setHasValidTokens(true);
      return {
        validTokens: [],
        invalidTokens: [],
        filledTokensNotInTemplate: [],
        nonFilledValidTokens: [],
        hasValidationIssues: false,
      };
    }

    const filledValues = getFilledValues({
      detailsFormConfig,
      detailsFormRef,
      offerPayload,
      overrideValues: sendModeFillValues,
    });

    // Multi-doc: template.tokens is null (tokens live per-doc). Any doc with invalid or
    // unfilled tokens blocks the Next gate; the per-doc Info badge reads displayValidation.
    if (isMultiDoc) {
      const anyDocHasIssues = docs.some((doc) => {
        const { invalidTokens, nonFilledValidTokens } = extractTokenArrays({
          templateTokens: getUpdatedTokens(doc.tokens || {}),
          filledValues,
        });
        return (
          (invalidTokens?.length || 0) > 0 ||
          (nonFilledValidTokens?.length || 0) > 0
        );
      });
      // Token validity is global, so status is the same across docs — merging is a safe
      // union. Keep the full aggregate so EmptyTokensWarning/Form get the real tokens.
      const allDocTokens = docs.reduce(
        (acc, doc) => ({ ...acc, ...getUpdatedTokens(doc.tokens || {}) }),
        {}
      );
      const aggregate = extractTokenArrays({
        templateTokens: allDocTokens,
        filledValues,
      });
      setHasValidTokens(!anyDocHasIssues);
      return {
        validTokens: aggregate.validTokens || [],
        invalidTokens: aggregate.invalidTokens || [],
        filledTokensNotInTemplate: aggregate.filledTokensNotInTemplate || [],
        nonFilledValidTokens: aggregate.nonFilledValidTokens || [],
        hasValidationIssues: anyDocHasIssues,
      };
    }

    const tokenArrays = extractTokenArrays({
      templateTokens: getUpdatedTokens(currentTemplate?.tokens),
      filledValues,
    });

    const hasInvalidTokens =
      tokenArrays.invalidTokens?.length > 0 ||
      tokenArrays.nonFilledValidTokens?.length > 0;

    setHasValidTokens(!hasInvalidTokens);

    return {
      ...tokenArrays,
      hasValidationIssues: hasInvalidTokens,
    };
  }, [
    currentTemplate,
    docs,
    isMultiDoc,
    detailsFormConfig,
    detailsFormRef,
    setHasValidTokens,
    offerPayload,
    sendModeFillValues,
    sendModeTokensFilled,
  ]);

  // Memoize separately so switching tabs (selectedDoc change) doesn't also rerun
  // when form values (offerPayload/detailsFormConfig) change.
  const selectedDocTokens = useMemo(
    () => getUpdatedTokens(selectedDoc?.tokens || {}),
    [selectedDoc]
  );

  // Per-selected-doc token sections for display. The aggregate `tokenValidation`
  // above stays the source of truth for generation + the Next gate (worst-of all docs).
  const displayValidation = useMemo(() => {
    if (!isMultiDoc) return tokenValidation;
    // Saved empty-token values don't refetch into offerPayload, so clear the banner arrays to
    // drop the stale "Fields not filled" warning (CORE-4792) but keep the Info tag (CORE-4822 follow-up).
    if (sendModeTokensFilled) {
      const filledValues = getFilledValues({
        detailsFormConfig,
        detailsFormRef,
        offerPayload,
        overrideValues: sendModeFillValues,
      });
      const recomputed = extractTokenArrays({
        templateTokens: selectedDocTokens,
        filledValues,
      });
      return {
        validTokens: [],
        // Invalid tokens come from the template itself (unrecognized variables), not from
        // unfilled fields, so filling fields can't resolve them — keep showing them, e.g.
        // after replacing a doc with a template that has invalid tokens (CORE-4851).
        invalidTokens: recomputed.invalidTokens || [],
        nonFilledValidTokens: [],
        filledTokensNotInTemplate: recomputed.filledTokensNotInTemplate || [],
      };
    }
    const filledValues = getFilledValues({
      detailsFormConfig,
      detailsFormRef,
      offerPayload,
      overrideValues: sendModeFillValues,
    });
    return extractTokenArrays({
      templateTokens: selectedDocTokens,
      filledValues,
    });
  }, [
    isMultiDoc,
    sendModeTokensFilled,
    selectedDocTokens,
    tokenValidation,
    detailsFormConfig,
    detailsFormRef,
    offerPayload,
    sendModeFillValues,
  ]);

  // Missing fields whose edit needs re-approval → "Edit offer" routes to the full drawer.
  // Only when offer_approval_enabled; read the shared cache with refetchOnMount:false to avoid a mount-refetch loop.
  const { data: approvalSettings } = useGetApprovalSettingsQuery({
    refetchOnMount: false,
  });
  const offerApprovalEnabled = !!approvalSettings?.offer_approval_enabled;

  const missingReapprovalTokens = useMemo(() => {
    if (!offerApprovalEnabled) return [];
    const tokenSet = new Set([
      ...(tokenValidation.nonFilledValidTokens || []),
      ...(tokenValidation.invalidTokens || []),
    ]);
    return (detailsFormConfig || [])
      .flatMap((section: any) => section?.fields ?? [section])
      .filter(
        (field: any) =>
          field?.preapproval_enabled &&
          field?.tokenName &&
          tokenSet.has(field.tokenName)
      )
      .map((field: any) => field.tokenName);
  }, [detailsFormConfig, tokenValidation, offerApprovalEnabled]);

  const handleGoToDetails = () => {
    if (missingReapprovalTokens.length > 0 && onEditOffer) {
      onEditOffer(missingReapprovalTokens);
    } else {
      setShowEditForm(true);
    }
  };

  // Clear generation errors on re-generate/doc change. isPreviewReady is left to
  // DocumentPreview (via onPreviewReady) so the fallback toolbar never flashes.
  useEffect(() => {
    setGenerationErrorIndices(new Set());
  }, [regenerateKey, docs]);

  // Per-tab error tooltip messages for the DocumentTabs rail.
  const perDocErrors = useMemo(() => {
    if (!isMultiDoc) return undefined;
    const filledValues = getFilledValues({
      detailsFormConfig,
      detailsFormRef,
      offerPayload,
      overrideValues: sendModeFillValues,
    });
    return docs.map((doc, i) => {
      // Saved empty-token values don't refetch into offerPayload, so suppress stale token
      // errors — only a real generation failure flags a tab (CORE-4792).
      if (!sendModeTokensFilled) {
        const { invalidTokens, nonFilledValidTokens } = extractTokenArrays({
          templateTokens: getUpdatedTokens(doc.tokens || {}),
          filledValues,
        });
        const hasInvalid = (invalidTokens?.length || 0) > 0;
        const hasUnfilled = (nonFilledValidTokens?.length || 0) > 0;
        if (hasInvalid && hasUnfilled)
          return 'Contains invalid tokens and unfilled fields';
        if (hasInvalid) return 'Contains invalid tokens';
        if (hasUnfilled) return 'Fields not filled';
      }
      if (generationErrorIndices.has(i))
        return 'Failed to generate — click to retry';
      return undefined;
    });
  }, [
    isMultiDoc,
    sendModeTokensFilled,
    docs,
    detailsFormConfig,
    detailsFormRef,
    offerPayload,
    sendModeFillValues,
    generationErrorIndices,
  ]);

  // True whenever the actions row (Info badge) should be visible for the selected doc.
  const showDocActionsRow =
    isGenerating ||
    (displayValidation?.nonFilledValidTokens?.length ?? 0) > 0 ||
    (displayValidation?.invalidTokens?.length ?? 0) > 0 ||
    (displayValidation?.filledTokensNotInTemplate?.length ?? 0) > 0 ||
    generationErrorIndices.has(selectedDocIndex);

  // Whether the selected doc has its own token issues. Generation is gated on the aggregate,
  // so this only picks the message: own token banner vs pointer to a blocking doc (CORE-4784).
  const selectedDocHasIssues = isMultiDoc
    ? (displayValidation.invalidTokens?.length || 0) > 0 ||
      (displayValidation.nonFilledValidTokens?.length || 0) > 0
    : tokenValidation.hasValidationIssues;

  // Because we now fetch a paginated/searched slice (not the full library), the
  // currently-selected template may fall outside the active page. Keep it in the
  // option list so the dropdown always renders its name (mainly the inline
  // per-offer case; library single-doc copies hide the dropdown entirely).
  const optionTemplates = useMemo(() => {
    const list = [...templates];
    const selected = currentTemplate;
    if (
      selected?.id &&
      selected.kind === 'inline' &&
      !list.some((t) => t?.id === selected.id)
    ) {
      return [selected, ...list];
    }
    return list;
  }, [templates, currentTemplate]);

  // Apply a template to this offer (optimistic swap → PUT → replace with the
  // per-offer copy from the BE). Shared by the "Select offer letter template"
  // dropdown and the "Add from existing template" submenu on "Add more files"
  // so both routes behave identically. `id` is the template id.
  const applyTemplateSelection = useCallback(
    async (id) => {
      if (!id || id === 'custom') return;
      const selectedTemplate = optionTemplates?.find((i) => i?.id === id);
      if (!selectedTemplate) return;
      // Same monotonic guard as applyTemplateMutation: a stale PUT resolving late must not
      // overwrite the newer template or mark selection complete (CORE-4821 / sanity #3, BLOCKER).
      const seq = ++applyOpSeqRef.current;
      setTemplateProps((prev) => ({
        ...prev,
        selected_template_id: id,
        template_value: selectedTemplate,
      }));
      setRegenerateKey((prev) => prev + 1);
      setIsTemplateSelectionComplete(false);
      setShowEditForm(false);
      // New template has its own tokens — clear the "already filled" flag so its
      // required/empty fields are re-validated (CORE-4822).
      setSendModeTokensFilled(false);

      // Call the API to update the offer with the selected template.
      // The callback returns the per-offer template copy from the BE response,
      // which has per-doc token validity data that the library template lacks.
      // Replace the optimistically-set library template so tokenValidation uses
      // accurate token data when computing filledTokensNotInTemplate.
      try {
        if (onTemplateSelect) {
          const freshTemplate = await onTemplateSelect(id, selectedTemplate);
          // A newer template change superseded us while the PUT was in flight.
          if (seq !== applyOpSeqRef.current) return;
          if (
            freshTemplate &&
            typeof freshTemplate === 'object' &&
            freshTemplate.id
          ) {
            setTemplateProps((prev) => ({
              ...prev,
              template_value: freshTemplate,
            }));
          }
        }
        if (seq === applyOpSeqRef.current) {
          setIsTemplateSelectionComplete(true);
        }
      } catch (error) {
        if (seq === applyOpSeqRef.current) {
          setIsTemplateSelectionComplete(true);
        }
        throw error;
      }
    },
    [
      optionTemplates,
      onTemplateSelect,
      setTemplateProps,
      setRegenerateKey,
      setIsTemplateSelectionComplete,
      setShowEditForm,
      setSendModeTokensFilled,
    ]
  );

  // Verified templates only — the offer can only be generated from a verified
  // template, matching the dropdown's `isDisabled: status !== 'verified'`.
  // documentUrl/filename come from the template's stored .docx so the picker can
  // attach it as a document (same flow as an upload) instead of replacing.
  const selectableTemplates = useMemo(
    () =>
      optionTemplates
        .filter((t) => t?.status === 'verified')
        .map((t) => {
          const docs = t?.offer_documents?.length
            ? t.offer_documents
            : t?.offer_document
              ? [t.offer_document]
              : [];
          return {
            id: t.id,
            name: t?.name,
            documents: docs.map((d) => ({
              url: d?.url,
              filename: d?.filename,
              byteSize: d?.byte_size,
            })),
          };
        }),
    [optionTemplates]
  );

  // Form configuration
  const formConfig = useMemo(
    () => [
      {
        type: 'kSelect',
        name: 'ats_offer_template_id',
        label: 'Select offer letter template',
        placeholder: 'Select template',
        isRequired: true,
        // Search hits the BE by template name; keep the react-select input in
        // sync so results replace the current page instead of client-filtering it.
        customOnInputChange: handleTemplateInputChange,
        options: [
          ...(isCustomTemplate
            ? [
                {
                  label: 'Custom',
                  value: 'custom',
                  desc: 'Customised for this offer',
                  isDisabled: true,
                },
              ]
            : []),
          // Split verified (selectable) templates from the rest while keeping
          // the BE's newest-first order within each group: newest verified
          // first, then the remaining unverified ones. Stable + valid — the old
          // `verified ? -1 : 1` comparator ignored b, scrambling that order and
          // burying the just-created template.
          ...[...optionTemplates]
            .sort(
              (a, b) =>
                (a?.status === 'verified' ? 0 : 1) -
                (b?.status === 'verified' ? 0 : 1)
            )
            .map((temp) => ({
              label: temp?.name,
              value: temp?.id,
              desc: (() => {
                // Multi-doc templates carry tokens per-doc, not at top level.
                const docTokenCount = (temp?.offer_documents ?? []).reduce(
                  (sum, d) => sum + Object.keys(d?.tokens ?? {}).length,
                  0
                );
                const hasTokens =
                  Object.keys(temp?.tokens ?? {}).length > 0 ||
                  docTokenCount > 0;
                return hasTokens
                  ? STATUS_CONFIG[temp?.status]
                  : STATUS_CONFIG['no_tokens'];
              })(),
              isDisabled: temp?.status !== 'verified',
            })),
        ],
        isLoading: isFetching,
        descKey: 'desc',
        components: {
          Option: (props) =>
            props.data.value === 'custom' ? null : (
              <SelectWithDescriptionOption {...props} />
            ),
        },
        menuPortalTarget: document.body,
        customStyles: {
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
          }),
        },
        customOnChange: (value) => applyTemplateSelection(value?.value),
      },
    ],
    [
      optionTemplates,
      isFetching,
      isCustomTemplate,
      handleTemplateInputChange,
      applyTemplateSelection,
    ]
  );

  const handleRegenerate = () => {
    setRegenerateKey((prev) => prev + 1);
  };

  if (uploadReplacement.isPending) {
    return <CenterSpinner h="400px" />;
  }

  return (
    <Box h="100%" w="100%">
      {/* Selection dropdown: always shown so the template can be re-selected in edit.
          dropdownTemplateValue surfaces the matched library name or "Custom". */}
      <Box mt="-12px">
        <FormBuilder
          initialValues={initialValues?.templates}
          config={formConfig}
          formRef={templatesFormRef}
        />
      </Box>

      {/* Template Content */}
      {currentTemplate?.id && (
        <Flex
          w="100%"
          minH="100%"
          h="100%"
          {...(useDocNav && {
            borderTop: '1px solid',
            borderRight: '1px solid',
            borderColor: 'border-color',
            pt: '10px',
          })}
        >
          {useDocNav && (
            <DocumentTabs
              docs={docs}
              selectedIndex={selectedAttachmentId ? -1 : selectedDocIndex}
              onSelect={(index) => {
                setSelectedDocIndex(index);
                setSelectedAttachmentId(null);
              }}
              onRemoveDocument={isMultiDoc ? handleRemoveDocument : undefined}
              attachments={offerAttachments}
              onRemoveAttachment={handleRemoveAttachment}
              selectedAttachmentId={selectedAttachmentId}
              onSelectAttachment={setSelectedAttachmentId}
              footer={
                <AddMoreFilesButton
                  offerId={currentOffer?.id}
                  variant="quaternary"
                  templates={selectableTemplates}
                  existingDocs={docs}
                  onDocumentAdded={applyDocumentAddition}
                />
              }
              docErrors={perDocErrors}
            />
          )}
          <Box
            flex="1"
            minW="0"
            h="100%"
            w="100%"
            px={multiDocsEnabled ? '0' : '10px'}
          >
            {/* Single Template Upload Display. kind "single" means per-offer (not one-document),
                so skip this card whenever the doc rail is visible (flag on). */}
            {currentTemplate?.kind === 'single' && !useDocNav && (
              <Flex
                p="12px"
                border="1px solid"
                borderColor="border-color"
                borderRadius="sm"
                gap="12px"
                justifyContent="space-between"
                alignItems="center"
                mb="12px"
              >
                <Flex gap="12px" flex="1" alignItems="center" overflow="hidden">
                  <Center
                    minH="44px"
                    minW="44px"
                    bgColor="secondary-50"
                    borderRadius="sm"
                  >
                    <File04 size={20} />
                  </Center>
                  <TruncatedTextWithTooltip
                    {...{ text: currentTemplate?.offer_document?.filename }}
                  />
                </Flex>
                <Flex gap="12px" alignItems="center">
                  {(selectedDoc?.url ||
                    currentTemplate?.offer_document?.url) && (
                    <Box
                      as="button"
                      type="button"
                      cursor="pointer"
                      display="flex"
                      alignItems="center"
                      color="secondary-700"
                      aria-label="Preview document"
                      onClick={previewSelectedDoc}
                    >
                      <Eye size={16} />
                    </Box>
                  )}
                  <Center
                    p="6px 12px"
                    bgColor="pastel-2-light"
                    borderRadius="sm"
                    alignItems="center"
                    gap="6px"
                  >
                    <CheckCircle size={16} color="success-300" />
                    <KText color="success-300">Uploaded</KText>
                  </Center>
                  <Box
                    as="button"
                    type="button"
                    cursor="pointer"
                    display="flex"
                    alignItems="center"
                    onClick={() => {
                      setTemplateProps((prev) => ({
                        ...prev,
                        selected_template_id: null,
                        template_value: {},
                      }));
                    }}
                  >
                    <XClose size={16} />
                  </Box>
                </Flex>
              </Flex>
            )}

            {/* Send/inline-fill flow: show the inline fill form only for UNFILLED fields. A
                template with invalid tokens can't be fixed by filling fields (they're bad template
                variables), so those fall through to the preview area's invalid-tokens display —
                same as the generation flow — where the doc can be re-uploaded (CORE-4851/4825). */}
            {sendMode &&
            tokenValidation.hasValidationIssues &&
            !sendModeTokensFilled &&
            (tokenValidation.invalidTokens?.length || 0) === 0 ? (
              // No re-approval field missing → skip the warning card and show the inline form
              // directly; otherwise keep the card (its "Edit offer" routes to the Details → Approval drawer).
              showEditForm || missingReapprovalTokens.length === 0 ? (
                <EmptyTokensForm
                  nonFilledValidTokens={tokenValidation.nonFilledValidTokens}
                  detailsFormConfig={detailsFormConfig}
                  currentOffer={currentOffer}
                  applicationId={currentOffer?.application_id}
                  existingFormValues={existingFormValues}
                  onCancel={() => {
                    setShowEditForm(false);
                  }}
                  onSaveSuccess={(savedValues) => {
                    // Accumulate the inline-filled values so a later template switch
                    // re-validates against them — kept separate from the defaults-laden
                    // offerPayload/form ref so only genuine user input counts as filled
                    // (CORE-4822).
                    if (savedValues) {
                      setInlineSavedValues((prev) => ({
                        ...prev,
                        ...savedValues,
                      }));
                      setOfferPayload({
                        ...(offerPayload || {}),
                        ...savedValues,
                      });
                    }
                    setSendModeTokensFilled(true);
                    setHasValidTokens(true);
                    handleRegenerate();
                  }}
                />
              ) : (
                <EmptyTokensWarning
                  nonFilledValidTokens={tokenValidation.nonFilledValidTokens}
                  invalidTokens={tokenValidation.invalidTokens}
                  detailsFormConfig={detailsFormConfig}
                  onGoToDetails={handleGoToDetails}
                  onDownload={() =>
                    downloadFileFromUrl({
                      // Multi-doc keeps files in offer_documents (no single offer_document),
                      // so fall back to the selected doc (CORE-4810).
                      url:
                        selectedDoc?.url ||
                        currentTemplate?.offer_document?.url,
                      filename: toDocxFilename(
                        selectedDoc?.filename ||
                          currentTemplate?.offer_document?.filename
                      ),
                      useDirect: true,
                    })
                  }
                  ReplacementUploader={ReplacementUploader}
                  isUploadLoading={uploadReplacement.isPending}
                />
              )
            ) : (
              <>
                {/* Additional file preview. The template preview below stays mounted (hidden)
                    so switching back doesn't re-run generation (CORE-4681). */}
                {selectedAttachment && (
                  <Box
                    h={multiDocsEnabled ? '100%' : 'calc(100% - 120px)'}
                    w="100%"
                  >
                    <PDFReaderBrowser
                      file={selectedAttachment.url}
                      content_type={
                        selectedAttachment.content_type || 'application/pdf'
                      }
                      preview_url={null}
                      showReplaceControl={false}
                      handleUpload={() => {}}
                      acceptedFiles={[]}
                      maxFiles={1}
                      fileName={selectedAttachment.filename}
                      isCompact={multiDocsEnabled}
                      containerStyles={{ h: '100%' }}
                    />
                  </Box>
                )}

                <Box display={selectedAttachment ? 'none' : 'contents'}>
                  {/* Fallback toolbar — hidden once the preview shows its own. */}
                  {!isPreviewReady && (
                    <Flex
                      gap="12px"
                      alignItems="center"
                      justifyContent="space-between"
                      px={multiDocsEnabled ? '10px' : undefined}
                    >
                      {showDocActionsRow && (
                        <TokenValidation
                          filledTokensNotInTemplate={
                            displayValidation?.filledTokensNotInTemplate
                          }
                        />
                      )}
                      <Flex ml="auto" gap="8px" alignItems="center">
                        {/* Faithful preview — server-rendered PDF from the BE,
                            works in both flag modes (multi-doc + single-doc). */}
                        {(isMultiDoc
                          ? selectedDoc?.url
                          : currentTemplate?.offer_document?.url) && (
                          <Box
                            as="button"
                            type="button"
                            cursor="pointer"
                            display="flex"
                            alignItems="center"
                            color="secondary-700"
                            aria-label="Preview document"
                            onClick={previewSelectedDoc}
                          >
                            <Eye size={18} />
                          </Box>
                        )}
                        <DocViewerToolbar
                          onDownload={() =>
                            downloadFileFromUrl({
                              url: isMultiDoc
                                ? selectedDoc?.url
                                : currentTemplate?.offer_document?.url,
                              filename: toDocxFilename(
                                isMultiDoc
                                  ? selectedDoc?.filename
                                  : currentTemplate?.offer_document?.filename
                              ),
                              useDirect: true,
                            })
                          }
                          onReplace={handleReplaceUpload}
                          isReplaceLoading={uploadReplacement.isPending}
                        />
                      </Flex>
                    </Flex>
                  )}

                  {/* Token Validation Display (per selected document) */}
                  {(displayValidation?.nonFilledValidTokens?.length > 0 ||
                    displayValidation?.invalidTokens?.length > 0) && (
                    <Box>
                      <TokenValidation
                        validTokens={displayValidation.validTokens}
                        invalidTokens={displayValidation.invalidTokens}
                        nonFilledValidTokens={
                          displayValidation.nonFilledValidTokens
                        }
                      />
                    </Box>
                  )}

                  {/* All docs generate together, so any doc's tokens block the rest; when the
                      selected doc is clean, point to the flagged docs (CORE-4784). Generation flow only (CORE-4825). */}
                  {isMultiDoc &&
                    !sendMode &&
                    tokenValidation.hasValidationIssues &&
                    !selectedDocHasIssues && (
                      <EmptyResults
                        icon={DocErrorAsset}
                        wrapperProps={{
                          minH: '300px',
                          minW: '500px',
                        }}
                        iconProps={{ mb: '0px !important' }}
                        subText="Preview unavailable"
                        text="All documents need to be verified to generate and preview the offer letter"
                      />
                    )}

                  {/* Send flow: a replaced doc with invalid tokens gets the same doc-error
                      illustration as the generation flow (below the invalid-token list). Only
                      here — the inline fill + re-approval screens render in the other branch and
                      don't need it (CORE-4851). */}
                  {isMultiDoc &&
                    sendMode &&
                    (displayValidation?.invalidTokens?.length || 0) > 0 && (
                      <EmptyResults
                        icon={DocErrorAsset}
                        wrapperProps={{
                          minH: '300px',
                          minW: '500px',
                        }}
                        iconProps={{ mb: '0px !important' }}
                        subText="Preview unavailable"
                        text="This template has invalid tokens. Replace it with a corrected template to generate and preview the offer letter"
                      />
                    )}

                  {/* Document Preview */}
                  <DocumentPreview
                    offerId={currentOffer?.id}
                    templateId={templateProps?.selected_template_id}
                    hasValidationIssues={
                      sendMode
                        ? // Invalid tokens on the selected doc block generation even after
                          // fields were filled — a bad template can't produce a valid doc,
                          // so show the invalid-tokens list, not a stale preview (CORE-4851).
                          (displayValidation?.invalidTokens?.length || 0) > 0 ||
                          (!sendModeTokensFilled &&
                            tokenValidation.hasValidationIssues)
                        : tokenValidation.hasValidationIssues
                    }
                    onRegenerateTrigger={handleRegenerate}
                    regenerateKey={regenerateKey}
                    isTemplateSelectionComplete={isTemplateSelectionComplete}
                    isMultiDoc={isMultiDoc}
                    selectedDocIndex={selectedDocIndex}
                    pdfRemovalIndex={pdfRemoval.index}
                    pdfRemovalNonce={pdfRemoval.nonce}
                    onReplace={handleReplaceUpload}
                    showReplace={true}
                    fileName={
                      isMultiDoc
                        ? selectedDoc?.filename
                        : currentTemplate?.offer_document?.filename
                    }
                    onGenerationError={(idx) =>
                      setGenerationErrorIndices((prev) =>
                        new Set(prev).add(idx)
                      )
                    }
                    onPreviewReady={setIsPreviewReady}
                    toolbarLeftControl={
                      <TokenValidation
                        filledTokensNotInTemplate={
                          displayValidation?.filledTokensNotInTemplate
                        }
                      />
                    }
                  />
                </Box>
              </>
            )}
          </Box>
        </Flex>
      )}
      {esignReplaceDisclosure.isOpen &&
        (() => {
          const esignEnvelope = (currentOffer as any)?.esign_envelope;
          const nonVoidable = [
            'rejected_by_candidate',
            'rejected_by_company_signer',
            'accepted_by_candidate',
          ].includes(currentOffer?.offer_status ?? '');
          const providerMap: Record<string, { name: string; item: string }> = {
            docusign: { name: 'Docusign', item: 'envelope' },
            pandadocs: { name: 'PandaDoc', item: 'document' },
            adobe: { name: 'Adobe Sign', item: 'agreement' },
          };
          const provider = providerMap[esignEnvelope?.kind] ?? null;
          const headerText = provider
            ? nonVoidable
              ? `Upload replacement: Unable to void the ${provider.name} ${provider.item}`
              : `Note: This action will void the ${provider.name} ${provider.item}`
            : 'Upload replacement';
          const subHeaderText = provider
            ? nonVoidable
              ? undefined
              : `Uploading a replacement will void the existing ${provider.name} ${provider.item}. You can create a new ${provider.item} for this candidate after uploading.`
            : undefined;
          return (
            <ModalWithForm
              withForm={false}
              isOpen={esignReplaceDisclosure.isOpen}
              onClose={esignReplaceDisclosure.onClose}
              headerText={headerText}
              subHeaderText={subHeaderText}
              primaryCtaText="Continue to upload replacement"
              handleSubmit={confirmEsignReplace}
            />
          );
        })()}
      {previewNode}
    </Box>
  );
};
